import {
  type LeadApprovalRequestKind,
  type LeadStatusChangeRequestInboxItem,
  type LeadStatusChangeRequestStatusValue,
} from '@utazzo/shared';
import { UzBadge, UzButton } from '@utazzo/ui';
import { Check, Bell, ExternalLink, Info, X } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import {
  formatApprovalWhen,
  LeadApprovalReviewDialog,
  type LeadApprovalReviewAction,
} from '../components/LeadApprovalReviewDialog';
import { canReviewLeadApprovalRequest } from '../lead-approval-access';
import { formatStatusWithSubStatus } from '../stage-label';
import {
  useBulkApproveLeadApprovals,
  useLeadApprovalReviewers,
  useLeadApprovalsList,
  useLeadApprovalsPendingCount,
} from '../use-lead-approvals';
import { useAgentOptionsForLeadsWithRole } from '../use-lead-options';
import { useNudgeStatusChangeRequest } from '../use-leads';

import {
  DataGrid,
  type DataGridBulkAction,
  type DataGridColumn,
  type DataGridFilter,
} from '@/components/data-grid';
import { PageShell } from '@/components/page-shell';
import { useCurrentUser } from '@/features/auth/use-auth';
import { useDataGridState } from '@/hooks/use-data-grid-state';
import { LEAD_APPROVAL_STATUS_LABEL, humanEnum } from '@/lib/enum-labels';

// Won is intentionally omitted: winning no longer routes through this inbox as a
// filterable type. `kindLabel` + `parseKindFilter` still handle WON_TRANSITION so
// any historical Won rows and deep-links keep rendering.
const KIND_OPTIONS = [
  { value: '', label: 'All types' },
  { value: 'LOST_TRANSITION', label: 'LOST' },
  { value: 'DESTINATION_CHANGE', label: 'Destination' },
  { value: 'STATUS_CHANGE', label: 'Status change' },
] as const;

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
] as const;

/**
 * Opens on the "Pending" slice - the rows a reviewer actually has to action.
 * Stable reference (it's the nuqs parser default). Switching to any other
 * option, including "All", behaves as before.
 */
const DEFAULT_FILTERS: Readonly<Record<string, string>> = { status: 'PENDING' };

/** Short human label for every request kind. */
const kindLabel = (kind: LeadApprovalRequestKind): string => {
  switch (kind) {
    case 'DESTINATION_CHANGE':
      return 'Destination';
    case 'WON_TRANSITION':
      return 'Won';
    case 'STATUS_CHANGE':
      return 'Status change';
    default:
      return 'LOST';
  }
};

/**
 * Static filters. The "Requested by" filter is built inside the component
 * instead - its options come from `/users/assignable-agents`, which is
 * hierarchy-scoped server-side (ADMIN sees every active sales member,
 * SALES_LEAD sees themselves plus their direct reports). That happens to be
 * exactly the set of people whose requests the caller can review, so the
 * dropdown never lists somebody whose rows they cannot open.
 */
/** Role ordering + labels for the "Requested by" options (mirrors OwnerFilter). */
const MEMBER_ROLE_RANK: Record<string, number> = { SALES_LEAD: 0, SALES_AGENT: 1 };
const MEMBER_ROLE_LABEL: Record<string, string> = {
  SALES_LEAD: 'Team lead',
  SALES_AGENT: 'Sales agent',
};

const STATIC_FILTERS: ReadonlyArray<DataGridFilter> = [
  {
    id: 'kind',
    label: 'Type',
    options: KIND_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-40',
  },
  {
    id: 'status',
    label: 'Status',
    options: STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-36',
  },
];

/** 1h cooldown mirrors the API `NUDGE_COOLDOWN_MS`. */
const NUDGE_COOLDOWN_MS = 60 * 60 * 1000;

const nudgeCooldownRemainingMs = (lastNudgedAt: string | null): number => {
  if (!lastNudgedAt) return 0;
  const elapsed = Date.now() - new Date(lastNudgedAt).getTime();
  if (Number.isNaN(elapsed) || elapsed >= NUDGE_COOLDOWN_MS) return 0;
  return NUDGE_COOLDOWN_MS - elapsed;
};

const parseKindFilter = (value: string | undefined): LeadApprovalRequestKind | undefined => {
  if (
    value === 'LOST_TRANSITION' ||
    value === 'WON_TRANSITION' ||
    value === 'DESTINATION_CHANGE' ||
    value === 'STATUS_CHANGE'
  ) {
    return value;
  }
  return undefined;
};

/** Empty / unknown → undefined = "All" (every status). */
const parseStatusFilter = (
  value: string | undefined,
): LeadStatusChangeRequestStatusValue | undefined => {
  if (value === 'PENDING' || value === 'APPROVED' || value === 'REJECTED') return value;
  return undefined;
};

/**
 * Centralized pending approvals inbox (ADR-0054 hierarchy). Admins see every
 * request; sales leads see only rows assigned to them. Covers LOST transitions
 * and destination edits submitted by sales agents.
 *
 * 2026-07-22 (ADR-0069). Third mode: a REQUESTER view. Anyone who can reach
 * this page but cannot review (today: SALES_AGENT holding
 * `ops.lead_approvals.read`) sees only the requests they submitted - server
 * scoped - without Approve / Reject. Sales agents get a Nudge action on
 * PENDING rows to re-notify the assigned reviewer.
 */
export const LeadApprovalsPage = (): React.JSX.Element => {
  const navigate = useNavigate();
  const me = useCurrentUser();
  const isAdmin = me?.role === 'ADMIN';
  // Can this viewer review ANYTHING? Mirrors the per-row check below; used
  // for the page-level chrome (title, copy, filters) that can't be decided
  // row by row.
  const isReviewer = me?.role === 'ADMIN' || me?.role === 'SALES_LEAD';
  const isSalesAgent = me?.role === 'SALES_AGENT';
  const gridState = useDataGridState({
    defaultSortBy: 'requestedAt',
    defaultSortDir: 'desc',
    filterIds: ['kind', 'status', 'requestedBy', 'reviewedBy'],
    defaultFilters: DEFAULT_FILTERS,
  });
  const { state } = gridState;

  const statusFilter = parseStatusFilter(state.filters.status as string | undefined);
  const kindFilter = parseKindFilter(state.filters.kind as string | undefined);
  // '' (the "All members" option) → undefined = no filter.
  const requestedByFilter = (state.filters.requestedBy as string | undefined) || undefined;
  const reviewedByFilter = (state.filters.reviewedBy as string | undefined) || undefined;

  // Same hook the leads inbox OwnerFilter uses, so both surfaces show the same
  // roster and ordering. Returns [] until it resolves - the filter then just
  // renders with "All members" only.
  const { data: members } = useAgentOptionsForLeadsWithRole();
  const { data: reviewers } = useLeadApprovalReviewers(isReviewer);
  const filters = useMemo<ReadonlyArray<DataGridFilter>>(
    () =>
      // Requester view: every row is already the viewer's own, so the
      // people filters would only ever narrow to "me" / "nobody".
      !isReviewer
        ? STATIC_FILTERS
        : [
            ...STATIC_FILTERS,
            {
              id: 'requestedBy',
              label: 'Requested by',
              options: [
                { value: '', label: 'All members' },
                // Team leads first, then agents, alphabetical within each - same
                // ordering as the leads-inbox OwnerFilter. The role suffix keeps
                // the two Demo* accounts (and any same-named staff) distinguishable.
                ...[...members]
                  .sort(
                    (a, b) =>
                      (MEMBER_ROLE_RANK[a.role] ?? 99) - (MEMBER_ROLE_RANK[b.role] ?? 99) ||
                      a.fullName.localeCompare(b.fullName),
                  )
                  .map((m) => ({
                    value: m.id,
                    label: MEMBER_ROLE_LABEL[m.role]
                      ? `${m.fullName} (${MEMBER_ROLE_LABEL[m.role]})`
                      : m.fullName,
                  })),
              ],
              widthClass: 'w-48',
            },
            {
              id: 'reviewedBy',
              label: 'Approved by',
              // Reviewers come from the approvals table, so this list only ever
              // contains people who have actually actioned something. PENDING rows
              // have no reviewer - picking anyone here narrows to actioned requests.
              options: [
                { value: '', label: 'Anyone' },
                ...(reviewers ?? []).map((r) => ({ value: r.id, label: r.fullName })),
              ],
              widthClass: 'w-48',
            },
          ],
    [members, reviewers, isReviewer],
  );

  // Trimmed so a stray space doesn't become a term that matches nothing, and
  // '' → undefined so an empty box is "no search" rather than a filter on the
  // empty string (it also keeps the query key stable with the unsearched list).
  const searchTerm = state.q.trim() || undefined;

  const listQuery = useLeadApprovalsList({
    page: state.page,
    pageSize: state.pageSize,
    status: statusFilter,
    kind: kindFilter,
    requestedById: requestedByFilter,
    reviewedById: reviewedByFilter,
    search: searchTerm,
    sortBy: state.sortBy ?? undefined,
    sortDir: state.sortDir,
  });
  // Scoped to the "Requested by" filter so the header count always describes
  // the rows on screen - the Daily Tracker's Pending Approvals tile links
  // here with that filter pre-set, and a header counting the whole queue
  // would contradict the tile the operator just clicked.
  const countQuery = useLeadApprovalsPendingCount(true, requestedByFilter);
  const nudge = useNudgeStatusChangeRequest();
  const bulkApprove = useBulkApproveLeadApprovals();

  // Admin-only bulk approve for the multi-select bar. Non-pending rows in the
  // selection (e.g. picked while viewing "All") are dropped client-side - the
  // server also guards, but there is no point sending ids it will bounce.
  // No bulk reject: rejection needs a per-item reason.
  const approveBulkAction: DataGridBulkAction<LeadStatusChangeRequestInboxItem> = {
    id: 'bulk-approve',
    label: 'Approve selected',
    icon: <Check className="h-4 w-4" />,
    onAction: async (_ids, rows) => {
      const pending = rows.filter((r) => r.status === 'PENDING').map((r) => r.id);
      if (pending.length === 0) {
        toast.info('No pending requests selected.');
        return;
      }
      try {
        const res = await bulkApprove.mutateAsync(pending);
        const skipped = res.failed.length;
        toast.success(
          `Approved ${res.approved} request${res.approved === 1 ? '' : 's'}` +
            (skipped ? `. ${skipped} skipped.` : '.'),
        );
      } catch {
        // Error toast already shown by useBulkApproveLeadApprovals' onError.
      }
    },
  };

  // 2026-07-09. Read-only detail modal - opens on row-click and via the
  // "Details" action button. Shows every field on the inbox row + a link
  // to the lead detail page and, on PENDING rows, in-modal Approve /
  // Reject that mirror the row-level buttons so a reviewer never has to
  // close the modal to act. `action` non-null opens straight on the
  // confirmation step (the per-row Approve / Reject buttons).
  const [dialogTarget, setDialogTarget] = useState<{
    row: LeadStatusChangeRequestInboxItem;
    action: LeadApprovalReviewAction | null;
  } | null>(null);

  const rows = listQuery.data?.data ?? [];
  const pagination = listQuery.data
    ? {
        page: listQuery.data.pagination.page,
        pageSize: listQuery.data.pagination.pageSize,
        totalItems: listQuery.data.pagination.totalItems,
      }
    : null;
  const pendingCount = countQuery.data?.count ?? 0;

  const columns = useMemo((): ReadonlyArray<DataGridColumn<LeadStatusChangeRequestInboxItem>> => {
    const cols: DataGridColumn<LeadStatusChangeRequestInboxItem>[] = [
      {
        id: 'lead',
        header: 'Lead',
        cell: (row) => (
          <div className="flex flex-col">
            <span className="font-medium text-zinc-900">{row.leadReference}</span>
            <span className="text-xs text-zinc-500">{row.customerName ?? 'No customer name'}</span>
          </div>
        ),
      },
      {
        id: 'type',
        header: 'Type',
        width: '120px',
        cell: (row) => <span className="text-sm text-zinc-700">{kindLabel(row.kind)}</span>,
      },
      {
        id: 'transition',
        header: 'Requested change',
        cell: (row) =>
          row.kind === 'DESTINATION_CHANGE' ? (
            <div className="text-sm text-zinc-700">
              {row.fromDestination?.name ?? 'None'}
              {' → '}
              <span className="font-medium text-zinc-900">
                {row.toDestination?.name ?? 'Unknown'}
              </span>
            </div>
          ) : (
            <div className="text-sm text-zinc-700">
              {formatStatusWithSubStatus(row.fromStatus, row.fromSubStatus)}
              {' → '}
              <span className="font-medium text-zinc-900">
                {formatStatusWithSubStatus(row.toStatus, row.toSubStatus)}
              </span>
              {row.lostReason ? (
                <span className="block text-xs text-zinc-500">Reason: {row.lostReason.name}</span>
              ) : null}
            </div>
          ),
      },
      {
        id: 'requestedBy',
        header: 'Requested by',
        cell: (row) => (
          <div className="text-sm">
            <div className="text-zinc-900">{row.requestedBy.fullName}</div>
            {row.requestorNote ? (
              <div className="text-xs text-zinc-500">"{row.requestorNote}"</div>
            ) : null}
          </div>
        ),
      },
      {
        id: 'leadGenerated',
        header: 'Lead generated',
        width: '140px',
        // When the lead itself was created (lead.createdAt) - mirrors the
        // "Lead generated" row in the review dialog so reviewers see lead age.
        cell: (row) => (
          <span className="text-sm text-zinc-600">{formatApprovalWhen(row.leadCreatedAt)}</span>
        ),
      },
      {
        id: 'requestedAt',
        header: 'Submitted',
        width: '140px',
        cell: (row) => (
          <span className="text-sm text-zinc-600">{formatApprovalWhen(row.requestedAt)}</span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        width: '110px',
        cell: (row) => {
          const tone: 'success' | 'destructive' | 'secondary' =
            row.status === 'APPROVED'
              ? 'success'
              : row.status === 'REJECTED'
                ? 'destructive'
                : 'secondary';
          return (
            <UzBadge tone={tone} pill>
              {humanEnum(LEAD_APPROVAL_STATUS_LABEL, row.status)}
            </UzBadge>
          );
        },
      },
    ];

    if (isAdmin) {
      cols.push({
        id: 'reviewer',
        header: 'Reviewer',
        cell: (row) => (
          <span className="text-sm text-zinc-700">
            {row.assignedReviewer ? row.assignedReviewer.fullName : 'Admin pool'}
          </span>
        ),
      });
    }

    // Details action is always available (Pending + historical). Actions
    // cell renders the "View details" button + (Pending only) inline
    // Approve / Reject (reviewers) or Nudge (sales agents).
    cols.push({
      id: 'actions',
      header: '',
      // Wide enough for Approve / Reject (reviewer) or Nudge (sales agent).
      width: '280px',
      align: 'right',
      truncate: false,
      resizable: false,
      alwaysVisible: true,
      cell: (row) => {
        const cooldownMs = nudgeCooldownRemainingMs(row.lastNudgedAt);
        const nudgeOnCooldown = cooldownMs > 0;
        const cooldownMins = Math.ceil(cooldownMs / 60_000);
        return (
          <div className="flex justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
            <UzButton
              size="sm"
              variant="ghost"
              onClick={() => setDialogTarget({ row, action: null })}
              title="View request details"
            >
              <Info className="h-4 w-4" />
            </UzButton>
            <UzButton
              size="sm"
              variant="ghost"
              onClick={() => navigate(`/leads/${row.leadId}`)}
              title="Open lead detail"
            >
              <ExternalLink className="h-4 w-4" />
            </UzButton>
            {/* Sales agents only: remind the assigned reviewer while PENDING. */}
            {isSalesAgent && row.status === 'PENDING' && (
              <UzButton
                size="sm"
                variant="outline"
                disabled={nudge.isPending || nudgeOnCooldown}
                loading={nudge.isPending && nudge.variables?.requestId === row.id}
                title={
                  nudgeOnCooldown
                    ? `You can nudge again in about ${cooldownMins} minute${cooldownMins === 1 ? '' : 's'}.`
                    : 'Send a reminder notification to your approver'
                }
                onClick={() => nudge.mutate({ id: row.leadId, requestId: row.id })}
              >
                <Bell className="mr-1 h-4 w-4" />
                Nudge
              </UzButton>
            )}
            {/* Approve / Reject only for rows that are still PENDING - shown
              regardless of the status filter so a reviewer can act from the
              "All" view too. Gated per row on the same predicate the server
              enforces, so a requester-view viewer gets Details + Open lead
              only. */}
            {row.status === 'PENDING' && canReviewLeadApprovalRequest(row, me) && (
              <>
                <UzButton size="sm" onClick={() => setDialogTarget({ row, action: 'approve' })}>
                  <Check className="mr-1 h-4 w-4" />
                  Approve
                </UzButton>
                <UzButton
                  size="sm"
                  variant="outline"
                  onClick={() => setDialogTarget({ row, action: 'reject' })}
                >
                  <X className="mr-1 h-4 w-4" />
                  Reject
                </UzButton>
              </>
            )}
          </div>
        );
      },
    });

    return cols;
  }, [isAdmin, isSalesAgent, me, navigate, nudge]);

  const description = isAdmin
    ? 'Every LOST / Won / destination / status-change request across your admin pool - pending, approved and rejected. Approve or reject the ones still pending.'
    : isReviewer
      ? 'Every request submitted by your direct reports - pending, approved and rejected. Approve or reject the ones still pending.'
      : 'The approval requests you have submitted - pending, approved and rejected. Use Nudge on a pending row to remind your team lead or admin.';

  // A search that returns nothing is almost always the Status filter, which
  // opens on "Pending": the lead is findable, its request was just already
  // actioned. Say that instead of the generic "requests appear here when..."
  // copy, which reads as "no such lead" and sends the operator hunting.
  const emptyTitle = searchTerm
    ? `No approval requests match "${state.q.trim()}"`
    : isReviewer
      ? 'No approval requests'
      : 'You have no approval requests';
  const emptyDescription = searchTerm
    ? 'Searches cover the lead reference, lead title, customer name and requester. Set Status to "All" if the request you want has already been approved or rejected.'
    : isReviewer
      ? 'Requests appear here when a team member submits a LOST / Won / destination or status change. Adjust the Type / Status filters to narrow the list.'
      : 'A request appears here when you mark a lead as Lost or Won, change a destination, or move a lead back a stage. Adjust the Type / Status filters to narrow the list.';

  const title = isReviewer ? 'Approvals' : 'My approval requests';

  return (
    <PageShell
      title={pendingCount > 0 ? `${title} (${pendingCount} pending)` : title}
      description={description}
    >
      <DataGrid
        gridId="lead-approvals"
        data={rows}
        columns={columns}
        filters={filters}
        getRowId={(row) => row.id}
        isLoading={listQuery.isLoading}
        isError={listQuery.isError}
        onRetry={() => void listQuery.refetch()}
        // Row checkboxes + bulk bar are admin-only - SALES_LEAD reviewers keep
        // just the per-row Approve / Reject buttons (rejection needs a
        // per-item reason, so there is no bulk reject to offer them either).
        selectable={isAdmin}
        bulkActions={isAdmin ? [approveBulkAction] : undefined}
        pagination={pagination}
        state={state}
        onStateChange={gridState}
        onRowClick={(row) => setDialogTarget({ row, action: null })}
        searchPlaceholder="Search lead ref, customer or requester"
        emptyTitle={emptyTitle}
        emptyDescription={emptyDescription}
        tableLayout="auto"
      />

      {/* 2026-07-09. Read-only detail view. Opens on row-click OR via the
          per-row "Details" (Info) button, from PENDING and historical
          views alike. `action` non-null (per-row Approve / Reject) skips
          straight to the confirmation step. Shared with the lead detail
          header banner - see LeadApprovalReviewDialog. */}
      <LeadApprovalReviewDialog
        request={dialogTarget?.row ?? null}
        initialAction={dialogTarget?.action ?? null}
        onClose={() => setDialogTarget(null)}
        footerLeft={
          dialogTarget ? (
            <UzButton variant="ghost" onClick={() => navigate(`/leads/${dialogTarget.row.leadId}`)}>
              <ExternalLink className="mr-1 h-4 w-4" />
              Open lead
            </UzButton>
          ) : null
        }
      />
    </PageShell>
  );
};
